import { gql, useMutation } from "@apollo/client";
import {
  Card,
  CardContent,
  Divider,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useCallback, useMemo } from "react";
import { partition } from "lodash";

import AddRemoveList from "~/components/common/AddRemoveList";
import Help from "~/components/common/Help";
import ConfigureServiceLine from "./ConfigureServiceLine";
import { ErrandType, useOrgConfig, VisitType } from "~/providers/OrgConfigProvider";

type ServiceLineProps = {
  serviceLineId: number,
};

const ServiceLine = ({ serviceLineId }: ServiceLineProps) => {
  const orgConfig = useOrgConfig();
  const serviceLine = orgConfig.serviceLines[serviceLineId];

  const isVisitTypeConfigured = (vt: VisitType) => serviceLine?.visitTypeServiceLines.find(vtsl => vt.id === vtsl.visitTypeId)
  const [configuredVisitTypes, availableVisitTypes] = useMemo(() => partition(Object.values(orgConfig.visitTypes).filter(vt => vt !== undefined), isVisitTypeConfigured), [serviceLine, orgConfig]);
  const isErrandTypeConfigured = (errandType: ErrandType) =>
    serviceLine?.errandTypeServiceLines.find(link => errandType.id === link.errandTypeId);
  const [configuredErrandTypes, availableErrandTypes] = useMemo(
    () => partition(
      Object.values(orgConfig.errandTypes).filter(errandType => errandType !== undefined),
      isErrandTypeConfigured,
    ),
    [serviceLine, orgConfig],
  );

  const [createVTSL, { loading: loadingCreate }] = useMutation(CREATE_VISIT_TYPE_SERVICE_LINE);

  const onAdd = useCallback(async ([visitTypeId]: number[]) => {
    await createVTSL({
      variables: {
        serviceLineId,
        visitTypeId,
      },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig", "CommandIndexProviders"],
    });
  }, [serviceLineId]);

  const [deleteVTSL, { loading: loadingDelete }] = useMutation(DELETE_VISIT_TYPE_SERVICE_LINE);

  const onDelete = useCallback(async (visitTypeId: number) => {
    await deleteVTSL({
      variables: {
        visitTypeServiceLineId: serviceLine?.visitTypeServiceLines.find(vtsl => vtsl.visitTypeId === visitTypeId)?.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  }, [serviceLine]);

  const [createETSL, { loading: loadingCreateErrand }] = useMutation(CREATE_ERRAND_TYPE_SERVICE_LINE);

  const onAddErrand = useCallback(async ([errandTypeId]: number[]) => {
    await createETSL({
      variables: {
        serviceLineId,
        errandTypeId,
      },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  }, [serviceLineId]);

  const [deleteETSL, { loading: loadingDeleteErrand }] = useMutation(DELETE_ERRAND_TYPE_SERVICE_LINE);

  const onDeleteErrand = useCallback(async (errandTypeId: number) => {
    await deleteETSL({
      variables: {
        errandTypeServiceLineId: serviceLine?.errandTypeServiceLines
          .find(link => link.errandTypeId === errandTypeId)?.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
  }, [serviceLine]);

  if (!serviceLine) {
    return null;
  }

  return <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
    <Grid size={{ xs: 12 }}>
      <ConfigureServiceLine serviceLine={serviceLine} />
    </Grid>
    <Grid size={{ xs: 12, lg: 6 }}>
      <Card sx={{ height: "100%" }}>
        {/* Style is used to override :lastChild padding bottom */}
        <CardContent sx={{ p: 0, mb: 0 }} style={{ paddingBottom: 0 }}>
          <Typography sx={{ m: 2 }}>
            Visit Types
            <Help text="Select which visit types to allow for this service line." />
          </Typography>
          <Divider sx={{ mb: 2 }}></Divider>

          <AddRemoveList
            emptyLabel="No Visit Types Configured"
            items={configuredVisitTypes.map(vt => ({ key: vt.id, label: vt.name }))}
            addOptions={availableVisitTypes.map((vt) => ({ key: vt.id, label: vt.name }))}

            addLabel={`Add to ${serviceLine.name}`}
            onAdd={onAdd}
            addLoading={loadingCreate}

            onRemove={onDelete}
            removeLoading={loadingDelete}
          />
        </CardContent>
      </Card>
    </Grid>
    <Grid size={{ xs: 12, lg: 6 }}>
      <Card sx={{ height: "100%" }}>
        {/* Style is used to override :lastChild padding bottom */}
        <CardContent sx={{ p: 0, mb: 0 }} style={{ paddingBottom: 0 }}>
          <Typography sx={{ m: 2 }}>
            Errands
            <Help text="Select which errands to allow for this service line." />
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <AddRemoveList
            emptyLabel="No Errands Configured"
            items={configuredErrandTypes.map(errandType => ({
              key: errandType.id,
              label: errandType.name,
            }))}
            addOptions={availableErrandTypes.map(errandType => ({
              key: errandType.id,
              label: errandType.name,
            }))}
            addLabel={`Add to ${serviceLine.name}`}
            onAdd={onAddErrand}
            addLoading={loadingCreateErrand}
            onRemove={onDeleteErrand}
            removeLoading={loadingDeleteErrand}
          />
        </CardContent>
      </Card>
    </Grid>
  </Grid>
}

export default ServiceLine;

const CREATE_VISIT_TYPE_SERVICE_LINE = gql(`
  mutation CreateVisitTypeServiceLine(
    $visitTypeId: Int!
    $serviceLineId: Int!
  )
  @permission(requires: manageOrgConfig)
  {
    createVisitTypeServiceLine(
      visitTypeId: $visitTypeId
      serviceLineId: $serviceLineId
    ) {
      id
      visitTypeId
      serviceLineId
    }
  }
`);

const DELETE_VISIT_TYPE_SERVICE_LINE = gql(`
  mutation DeleteVisitTypeServiceLine(
    $visitTypeServiceLineId: Int!
  )
  @permission(requires: manageOrgConfig)
  {
    deleteVisitTypeServiceLine(
      id: $visitTypeServiceLineId
    ) {
      id
      deleted
    }
  }
`);

const CREATE_ERRAND_TYPE_SERVICE_LINE = gql(`
  mutation CreateErrandTypeServiceLine(
    $errandTypeId: Int!
    $serviceLineId: Int!
  )
  @permission(requires: manageOrgConfig)
  {
    createErrandTypeServiceLine(
      errandTypeId: $errandTypeId
      serviceLineId: $serviceLineId
    ) {
      id
      errandTypeId
      serviceLineId
    }
  }
`);

const DELETE_ERRAND_TYPE_SERVICE_LINE = gql(`
  mutation DeleteErrandTypeServiceLine(
    $errandTypeServiceLineId: Int!
  )
  @permission(requires: manageOrgConfig)
  {
    deleteErrandTypeServiceLine(
      id: $errandTypeServiceLineId
    ) {
      id
      deleted
    }
  }
`);
